import { useRequestsAPI } from '@innowise-group/core';
import { Modal, modalHoc, ModalProps, notificationsManager } from '@innowise-group/mui-kit';
import { useTranslation } from 'react-i18next';
import * as Styled from './request-deletion-modal.styles';

interface RequestDeletionModalProps {
  id: number;
  requestCloseCallback: () => void;
}

const RequestDeletionModal: React.FC<ModalProps & RequestDeletionModalProps> = ({
  close,
  id,
  requestCloseCallback,
}) => {
  const { t } = useTranslation();
  const { deleteRequest, fetchRequestById, fetchRequestCandidates, isLoading } = useRequestsAPI();

  // US-10.5 AC-3: a deleted request leads to the list with a notification
  const onDeleted = () => {
    notificationsManager.success({ subtitle: t('notifications.text.successRequestDeletion') });
    requestCloseCallback();
  };

  // US-10.5 AC-6: the card shows the actual request after a refusal, a request that is not available leads to the list
  const refreshRequest = async () => {
    try {
      await fetchRequestById(id).unwrap();
      fetchRequestCandidates(id);
    } catch {
      requestCloseCallback();
    }
  };

  const deleteRequestItem = async () => {
    close();
    try {
      await deleteRequest(id, onDeleted, { ignoreGlobalCatch: true }).unwrap();
    } catch (error) {
      // 404: the request was deleted by somebody else; 400: a candidate was attached after the card was loaded and the
      // backend tells it; any other refusal is the status changed in the meantime
      const isCandidatesRefusal = error?.status === 400 && !!error?.message;
      notificationsManager.error({
        subtitle: isCandidatesRefusal ? error.message : t('notifications.text.requestDeletionStatusChanged'),
      });
      if (error?.status === 404) {
        requestCloseCallback();
        return;
      }
      refreshRequest();
    }
  };

  return (
    <Modal.Container size="small">
      <Modal.Header onClose={close}>
        <Styled.ModalTitle>{t('pages.requests.deletionModal.requestDeletion')}</Styled.ModalTitle>
      </Modal.Header>
      <Modal.Body>
        <Styled.Description>{t('pages.requests.deletionModal.sureDelete')}?</Styled.Description>
      </Modal.Body>
      <Styled.Footer>
        <Styled.CancelButton onClick={close} variant="outlined">
          {t('buttons.cancel')}
        </Styled.CancelButton>
        <Styled.SubmitButton onClick={deleteRequestItem} disabled={isLoading}>
          {t('buttons.delete')}
        </Styled.SubmitButton>
      </Styled.Footer>
    </Modal.Container>
  );
};

export default modalHoc(RequestDeletionModal, 'request-deletion');
